import '@fontsource/geist/300.css';
import '@fontsource/geist/400.css';
import '@fontsource/geist/500.css';
import '@fontsource/geist/600.css';
import '@fontsource/geist/700.css';
import '@fontsource/geist-mono/300.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import '@fontsource/geist-mono/700.css';
import './styles/webflow.css';
import './styles/custom.css';
import './styles/interactions.css';
import { createMemoryHistory, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createRouter, showsNotFound } from './router';

const root = document.getElementById('root')!;

// Production pages are prerendered (scripts/prerender.ts) and hydrated; `vite dev` renders from scratch.
// index.html's root holds a placeholder comment in dev; prerendered pages have real markup.
const prerendered = root.firstElementChild !== null;

async function loadRouter(history?: Parameters<typeof createRouter>[0]) {
  const router = createRouter(history);
  // Render the same tree the server did. TanStack's own hydrate() sets this from state it
  // serializes into the page; the site never navigates client-side, so that state (which
  // would duplicate every page's content) isn't needed and the flag is set directly.
  if (prerendered) router.ssr = { manifest: undefined };
  await router.load();
  return router;
}

let router = await loadRouter();
// A URL with no page (no route matches, or e.g. a blog post that doesn't exist) is served
// 404.html, which was rendered from the /404 route. Render that route too (the address bar
// keeps the requested URL), so hydration finds the same tree.
if (prerendered && showsNotFound(router)) {
  router = await loadRouter(createMemoryHistory({ initialEntries: ['/404'] }));
}
const app = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
if (prerendered) hydrateRoot(root, app);
else createRoot(root).render(app);
